/**
 * Anonymous, cookieless visitor + session identifiers for first-party traffic
 * analytics. Nothing here is PII: the visitor id is a random UUID persisted in
 * localStorage (a per-device pseudonym), and the session id rolls over after 30
 * minutes of inactivity.
 */

const VISITOR_KEY = "ms_visitor_id";
const SESSION_KEY = "ms_session";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30-minute sliding window

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Fallback for older browsers / non-secure contexts.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — analytics is best-effort */
  }
}

/** Stable per-device anonymous id. Created once, reused forever. */
export function getVisitorId(): string {
  let id = safeGet(VISITOR_KEY);
  if (!id) {
    id = randomId();
    safeSet(VISITOR_KEY, id);
  }
  return id;
}

/**
 * Rolling session id. A new id is minted after 30 minutes of inactivity;
 * otherwise the current one is refreshed. Stored as `${id}:${lastSeenMs}`.
 */
export function getSessionId(): string {
  const now = Date.now();
  const raw = safeGet(SESSION_KEY);
  let id: string;

  if (raw) {
    const sep = raw.lastIndexOf(":");
    const prevId = sep > 0 ? raw.slice(0, sep) : "";
    const lastSeen = sep > 0 ? Number(raw.slice(sep + 1)) : NaN;
    if (prevId && Number.isFinite(lastSeen) && now - lastSeen < SESSION_TTL_MS) {
      id = prevId;
    } else {
      id = randomId();
    }
  } else {
    id = randomId();
  }

  safeSet(SESSION_KEY, `${id}:${now}`);
  return id;
}
